import { createEnv } from '@t3-oss/env-nextjs'
import * as v from 'valibot'

import { appEnvSchema, booleanFlagSchema, optionalStringSchema, resolveAppEnv } from './shared'

const optionalUrlSchema = v.optional(v.pipe(v.string(), v.url()))

export const clientEnv = createEnv({
  client: {
    NEXT_PUBLIC_ADYEN_CLIENT_KEY: optionalStringSchema,
    NEXT_PUBLIC_ADYEN_ENV: v.optional(v.picklist(['LIVE', 'TEST']), 'TEST'),
    NEXT_PUBLIC_APP_ENV: appEnvSchema,
    NEXT_PUBLIC_AWIN_ADVERTISER_ID: optionalStringSchema,
    NEXT_PUBLIC_CONSENT_BANNER_ID: optionalStringSchema,
    NEXT_PUBLIC_ENABLE_CHECKOUT_INTEGRATIONS: booleanFlagSchema,
    NEXT_PUBLIC_GROWTHBOOK_API_HOST: v.pipe(v.string(), v.trim(), v.minLength(1)),
    NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY: v.pipe(v.string(), v.trim(), v.minLength(1)),
    NEXT_PUBLIC_GROWTHBOOK_DECRYPTION_KEY: optionalStringSchema,
    NEXT_PUBLIC_GTM_ID: optionalStringSchema,
    NEXT_PUBLIC_IMGPROXY_URL: optionalStringSchema,
    NEXT_PUBLIC_INDEXING_ENABLED: booleanFlagSchema,
    NEXT_PUBLIC_MAP_TILER_KEY: optionalStringSchema,
    NEXT_PUBLIC_MAP_TILER_TEMPLATE: optionalStringSchema,
    NEXT_PUBLIC_PARTNER_HUB_URL: optionalUrlSchema,
    NEXT_PUBLIC_SHOP_RELEASE: optionalStringSchema,
    NEXT_PUBLIC_SHOP_URL: v.pipe(v.string(), v.url()),
    NEXT_PUBLIC_SHOW_REACT_QUERY_DEV_TOOLS: booleanFlagSchema,
    NEXT_PUBLIC_STORYBLOK_ACCESS_TOKEN: v.pipe(v.string(), v.trim(), v.minLength(1)),
    NEXT_PUBLIC_STORYBLOK_PREVIEW_ACCESS_TOKEN: optionalStringSchema,
  },
  runtimeEnv: {
    NEXT_PUBLIC_ADYEN_CLIENT_KEY: process.env.NEXT_PUBLIC_ADYEN_CLIENT_KEY,
    NEXT_PUBLIC_ADYEN_ENV: process.env.NEXT_PUBLIC_ADYEN_ENV,
    NEXT_PUBLIC_APP_ENV: resolveAppEnv(process.env.NEXT_PUBLIC_APP_ENV ?? process.env.APP_ENV),
    NEXT_PUBLIC_AWIN_ADVERTISER_ID: process.env.NEXT_PUBLIC_AWIN_ADVERTISER_ID,
    NEXT_PUBLIC_CONSENT_BANNER_ID: process.env.NEXT_PUBLIC_CONSENT_BANNER_ID,
    NEXT_PUBLIC_ENABLE_CHECKOUT_INTEGRATIONS: process.env.NEXT_PUBLIC_ENABLE_CHECKOUT_INTEGRATIONS,
    NEXT_PUBLIC_GROWTHBOOK_API_HOST: process.env.NEXT_PUBLIC_GROWTHBOOK_API_HOST,
    NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY: process.env.NEXT_PUBLIC_GROWTHBOOK_CLIENT_KEY,
    NEXT_PUBLIC_GROWTHBOOK_DECRYPTION_KEY: process.env.NEXT_PUBLIC_GROWTHBOOK_DECRYPTION_KEY,
    NEXT_PUBLIC_GTM_ID: process.env.NEXT_PUBLIC_GTM_ID,
    NEXT_PUBLIC_IMGPROXY_URL: process.env.NEXT_PUBLIC_IMGPROXY_URL,
    NEXT_PUBLIC_INDEXING_ENABLED: process.env.NEXT_PUBLIC_INDEXING_ENABLED,
    NEXT_PUBLIC_MAP_TILER_KEY: process.env.NEXT_PUBLIC_MAP_TILER_KEY,
    NEXT_PUBLIC_MAP_TILER_TEMPLATE: process.env.NEXT_PUBLIC_MAP_TILER_TEMPLATE,
    NEXT_PUBLIC_PARTNER_HUB_URL: process.env.NEXT_PUBLIC_PARTNER_HUB_URL,
    NEXT_PUBLIC_SHOP_RELEASE: process.env.NEXT_PUBLIC_SHOP_RELEASE,
    NEXT_PUBLIC_SHOP_URL: process.env.NEXT_PUBLIC_SHOP_URL,
    NEXT_PUBLIC_SHOW_REACT_QUERY_DEV_TOOLS: process.env.NEXT_PUBLIC_SHOW_REACT_QUERY_DEV_TOOLS,
    NEXT_PUBLIC_STORYBLOK_ACCESS_TOKEN: process.env.NEXT_PUBLIC_STORYBLOK_ACCESS_TOKEN,
    NEXT_PUBLIC_STORYBLOK_PREVIEW_ACCESS_TOKEN:
      process.env.NEXT_PUBLIC_STORYBLOK_PREVIEW_ACCESS_TOKEN,
  },
  emptyStringAsUndefined: true,
})

export function getPublicAdyenConfig() {
  if (!clientEnv.NEXT_PUBLIC_ADYEN_CLIENT_KEY) {
    throw new Error('NEXT_PUBLIC_ADYEN_CLIENT_KEY must be configured for checkout')
  }

  return {
    clientKey: clientEnv.NEXT_PUBLIC_ADYEN_CLIENT_KEY,
    environment: clientEnv.NEXT_PUBLIC_ADYEN_ENV === 'LIVE' ? 'live' : 'test',
  } as const
}

export type PublicAdyenConfig = ReturnType<typeof getPublicAdyenConfig>

export function getMapStyleUrl() {
  if (!clientEnv.NEXT_PUBLIC_MAP_TILER_KEY || !clientEnv.NEXT_PUBLIC_MAP_TILER_TEMPLATE) {
    throw new Error(
      'NEXT_PUBLIC_MAP_TILER_KEY and NEXT_PUBLIC_MAP_TILER_TEMPLATE must be configured for maps'
    )
  }

  return `https://api.maptiler.com/maps/${clientEnv.NEXT_PUBLIC_MAP_TILER_TEMPLATE}/style.json?key=${clientEnv.NEXT_PUBLIC_MAP_TILER_KEY}`
}
